/**
 * =============================================================================
 * tenantSimulator.ts - Simulazione tenant in locale (dev utility)
 * =============================================================================
 *
 * Permette di forzare l'app a comportarsi come un tenant diverso da quello
 * dell'utente loggato, utile per validare layout e branding multi-tenant
 * senza dover creare un account su ogni tenant.
 *
 * Attivazione:
 *   - Aggiungi `?tenant=<slug>` all'URL (es. ?tenant=beta) → salva in
 *     localStorage e ricarica.
 *   - Aggiungi `?tenant=` (vuoto) → rimuove l'override.
 *
 * Limiti:
 *   - Cambia solo il branding lato client (nome, logo, copyright, sitemap,
 *     llms.txt). Non altera l'isolamento RLS: le query restano filtrate
 *     dal tenant_id reale dell'utente in JWT.
 *   - Disponibile solo se import.meta.env.DEV oppure se è presente
 *     localStorage `__tenant_simulator_enabled__` = "1" (per QA su preview).
 * =============================================================================
 */

const STORAGE_KEY = "__tenant_override_slug__";
const ENABLE_KEY = "__tenant_simulator_enabled__";

/** True se la simulazione è abilitata in questo ambiente. */
function isSimulatorEnabled(): boolean {
  try {
    if (import.meta.env?.DEV) return true;
    return typeof window !== "undefined" &&
      window.localStorage?.getItem(ENABLE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Legge l'eventuale slug di override (URL ?tenant=... o localStorage). */
export function getTenantOverrideSlug(): string | null {
  if (typeof window === "undefined") return null;
  if (!isSimulatorEnabled()) return null;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("tenant")) {
      const v = url.searchParams.get("tenant")?.trim() ?? "";
      if (v === "") {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      window.localStorage.setItem(STORAGE_KEY, v);
      return v;
    }
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Helper per abilitare manualmente il simulatore (es. da console). */
export function enableTenantSimulator(): void {
  try {
    window.localStorage.setItem(ENABLE_KEY, "1");
  } catch {
    /* noop */
  }
}

/** Helper per disabilitare il simulatore e rimuovere l'override. */
export function disableTenantSimulator(): void {
  try {
    window.localStorage.removeItem(ENABLE_KEY);
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}